import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const ROLES = { admin: '👑 Administrador', conductor: '🚛 Conductor', despachador: '📋 Despachador', finanzas: '💰 Finanzas' };
const ROLES_COLORS = { admin: '#CC0000', conductor: '#1a6b3a', despachador: '#1e4d8c', finanzas: '#8b5cf6' };

const MODULOS_LABEL = {
  viajes: 'Viajes', tanques: 'Tanques', clientes: 'Clientes',
  vehiculos: 'Vehículos', conductores: 'Conductores',
  tipos_combustible: 'Combustibles', origenes_carga: 'Orígenes de Carga',
  excedentes: 'Excedentes', reportes: 'Reportes',
  usuarios: 'Usuarios', permisos: 'Permisos',
};
const ACCIONES_LABEL = { ver: 'Ver', agregar: 'Agregar', editar: 'Editar', borrar: 'Borrar' };

const Usuarios = ({ usuario, onLogout }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nombre: '', email: '', password: '', rol: 'conductor' });
  const [showPassword, setShowPassword] = useState(false);

  // ── Permisos (matriz al editar) ──
  const [permMatriz, setPermMatriz] = useState(null);       // matriz editable {modulo:{accion:bool}}
  const [permPlantilla, setPermPlantilla] = useState(null); // plantilla del rol (para marcar ajustes)
  const [permModulos, setPermModulos] = useState([]);
  const [permAcciones, setPermAcciones] = useState([]);
  const [permRolUsuario, setPermRolUsuario] = useState('');
  const [permSaving, setPermSaving] = useState(false);
  const [permMsg, setPermMsg] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/usuarios');
      setUsuarios(res.data);
    } catch { setError('Error cargando usuarios'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const resetForm = () => { setForm({ nombre: '', email: '', password: '', rol: 'conductor' }); setEditando(null); setShowForm(false); setError(''); setPermMatriz(null); setPermPlantilla(null); setPermMsg(''); };

  const abrirEditar = (u) => {
    setForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol });
    setEditando(u.id);
    setShowForm(true);
    cargarPermisos(u);
  };

  // ── Permisos: carga, edición y guardado de la matriz ──
  const cargarPermisos = async (u) => {
    setPermMatriz(null); setPermMsg('');
    try {
      const [rMat, rPla] = await Promise.all([
        api.get(`/permisos/usuario/${u.id}`),
        api.get('/permisos/plantillas'),
      ]);
      setPermMatriz(JSON.parse(JSON.stringify(rMat.data.matriz)));
      setPermModulos(rMat.data.modulos);
      setPermAcciones(rMat.data.acciones);
      setPermRolUsuario(rMat.data.rol);
      setPermPlantilla(rPla.data.plantillas[rMat.data.rol] || {});
    } catch {
      // Sin permiso para ver la matriz (no admin) → simplemente no se muestra
      setPermMatriz(null);
    }
  };

  const togglePermiso = (modulo, accion) => {
    setPermMatriz(prev => ({
      ...prev,
      [modulo]: { ...prev[modulo], [accion]: !prev[modulo][accion] },
    }));
    setPermMsg('');
  };

  const todosMarcados = permMatriz
    ? permModulos.every(m => permAcciones.every(a => permMatriz[m]?.[a]))
    : false;

  const marcarTodos = () => {
    const valor = !todosMarcados;
    setPermMatriz(prev => {
      const nueva = {};
      permModulos.forEach(m => {
        nueva[m] = {};
        permAcciones.forEach(a => { nueva[m][a] = valor; });
      });
      return nueva;
    });
    setPermMsg('');
  };

  const guardarPermisos = async () => {
    if (!editando || !permMatriz) return;
    setPermSaving(true); setPermMsg('');
    try {
      const lista = [];
      permModulos.forEach(m => permAcciones.forEach(a => {
        lista.push({ modulo: m, accion: a, permitido: !!permMatriz[m][a] });
      }));
      const res = await api.put(`/permisos/usuario/${editando}`, { permisos: lista });
      setPermMatriz(JSON.parse(JSON.stringify(res.data.matriz)));
      setPermMsg('✅ Permisos guardados');
    } catch (err) {
      const d = err?.response?.data?.detail;
      setPermMsg('⚠️ ' + (typeof d === 'string' ? d : 'Error guardando permisos'));
    } finally { setPermSaving(false); }
  };

  const guardar = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      if (editando) {
        const datos = { nombre: form.nombre, email: form.email, rol: form.rol };
        if (form.password && form.password.trim()) {
          datos.password = form.password;
        }
        await api.put(`/usuarios/${editando}`, datos);
      } else {
        await api.post('/usuarios', form);
      }
      resetForm();
      cargar();
    } catch (err) { setError(err.response?.data?.detail || 'Error guardando usuario'); }
    finally { setSaving(false); }
  };

  const desactivar = async (id) => {
    if (!window.confirm('¿Desactivar este usuario?')) return;
    try { await api.delete(`/usuarios/${id}`); cargar(); }
    catch { setError('Error desactivando usuario'); }
  };

  const activos = usuarios.filter(u => u.activo);
  const inactivos = usuarios.filter(u => !u.activo);

  return (
    <Layout usuario={usuario} onLogout={onLogout} title="Usuarios">
      <div style={S.headerRow}>
        <div style={S.info}>
          <span style={S.count}>{activos.length} activos</span>
          {inactivos.length > 0 && <span style={S.countInac}>· {inactivos.length} inactivos</span>}
        </div>
        <button style={S.btnAdd} onClick={() => setShowForm(true)}>+ Nuevo Usuario</button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {showForm && (
        <div style={editando && permMatriz ? S.dosPaneles : undefined}>
        <div style={S.formCard}>
          <h3 style={S.formTitle}>{editando ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
          <form onSubmit={guardar} style={S.form}>
            <div style={S.formGrid}>
              <div style={S.group}>
                <label style={S.label}>Nombre completo *</label>
                <input style={S.input} value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} placeholder="Nombre del usuario" required />
              </div>
              <div style={S.group}>
                <label style={S.label}>Email *</label>
                <input style={S.input} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="usuario@empresa.com" required />
              </div>
              <div style={S.group}>
                <label style={S.label}>{editando ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
                <div style={{ position: 'relative' }}>
                  <input style={{ ...S.input, paddingRight: '44px', width: '100%', boxSizing: 'border-box' }} type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={editando ? 'Dejar vacío para mantener la actual' : 'Mínimo 6 caracteres'} required={!editando} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', padding: 0, lineHeight: 1 }} tabIndex={-1} aria-label={showPassword ? 'Ocultar' : 'Mostrar'}>
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div style={S.group}>
                <label style={S.label}>Rol *</label>
                <select style={S.input} value={form.rol} onChange={e => setForm({...form, rol: e.target.value})}>
                  <option value="conductor">🚛 Conductor</option>
                  <option value="despachador">📋 Despachador</option>
                  <option value="finanzas">💰 Finanzas</option>
                  <option value="admin">👑 Administrador</option>
                </select>
              </div>
            </div>
            <div style={S.formBtns}>
              <button type="button" style={S.btnCancel} onClick={resetForm}>Cancelar</button>
              <button type="submit" style={S.btnSave} disabled={saving}>{saving ? 'Guardando...' : editando ? 'Guardar Cambios' : 'Crear Usuario'}</button>
            </div>
          </form>
        </div>

        {/* ── Panel de PERMISOS (solo al editar; estilo legacy) ── */}
        {editando && permMatriz && (
          <div style={S.formCard}>
            <h3 style={S.formTitle}>Permisos</h3>
            <div style={S.permSub}>Asignar Permisos al Usuario <span style={{color:'#888', fontWeight:'400'}}>(base: rol {ROLES[permRolUsuario] || permRolUsuario})</span></div>

            <label style={S.permTodos}>
              <input type="checkbox" checked={todosMarcados} onChange={marcarTodos} style={S.permCheck} />
              Marcar / Desmarcar Todos
            </label>

            <div style={S.permLista}>
              {permModulos.map(m => {
                const bloqueado = m === 'permisos' && permRolUsuario !== 'admin';
                return (
                  <div key={m} style={S.permFila}>
                    <div style={S.permModulo}>{MODULOS_LABEL[m] || m}</div>
                    <div style={S.permAcciones}>
                      {permAcciones.map(a => {
                        const marcado = !!permMatriz[m]?.[a];
                        const heredado = permPlantilla ? (!!permPlantilla[m]?.[a]) === marcado : true;
                        return (
                          <label key={a} style={{...S.permAccion, opacity: bloqueado ? 0.4 : 1}}>
                            <input
                              type="checkbox"
                              checked={marcado}
                              disabled={bloqueado}
                              onChange={() => togglePermiso(m, a)}
                              style={{...S.permCheck, accentColor: heredado ? '#CC0000' : '#b8860b'}}
                            />
                            <span style={{color: heredado ? '#333' : '#b8860b', fontWeight: heredado ? '500' : '700'}}>
                              {ACCIONES_LABEL[a]}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={S.permLeyenda}>
              <span style={{color:'#333'}}>■ heredado del rol</span>
              <span style={{color:'#b8860b', marginLeft:'14px'}}>■ ajuste manual</span>
            </div>

            {permMsg && <div style={S.permMsg}>{permMsg}</div>}

            <div style={{display:'flex', justifyContent:'flex-end', marginTop:'12px'}}>
              <button type="button" style={S.btnOtorgar} onClick={guardarPermisos} disabled={permSaving}>
                {permSaving ? 'Guardando...' : 'Otorgar Permisos'}
              </button>
            </div>
          </div>
        )}
        </div>
      )}

      {loading ? <div style={S.loading}>Cargando...</div> : usuarios.length === 0 ? (
        <div style={S.empty}><div style={{fontSize:'48px'}}>👥</div><p>No hay usuarios registrados.</p></div>
      ) : (
        <div style={S.list}>
          {usuarios.map(u => (
            <div key={u.id} style={{...S.card, ...(u.activo ? {} : S.cardInactivo)}}>
              <div style={S.cardLeft}>
                <div style={{...S.avatar, background: ROLES_COLORS[u.rol] || '#666'}}>
                  {u.nombre.charAt(0).toUpperCase()}
                </div>
                <div style={S.cardInfo}>
                  <div style={S.cardNombre}>{u.nombre}</div>
                  <div style={S.cardEmail}>{u.email}</div>
                  <span style={{...S.rolBadge, background: ROLES_COLORS[u.rol] + '20', color: ROLES_COLORS[u.rol]}}>
                    {ROLES[u.rol]}
                  </span>
                </div>
              </div>
              {u.id !== usuario.id && u.activo && (
                <div style={S.cardBtns}>
                  <button style={S.btnEdit} onClick={() => abrirEditar(u)}>✏️</button>
                  <button style={S.btnDel} onClick={() => desactivar(u.id)}>🗑️</button>
                </div>
              )}
              {u.id === usuario.id && (
                <span style={S.youBadge}>Tú</span>
              )}
              {!u.activo && <span style={S.inactivoBadge}>Inactivo</span>}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

const S = {
  loading: {textAlign:'center', padding:'60px', color:'#666'},
  headerRow: {display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'},
  info: {display:'flex', gap:'8px', alignItems:'center'},
  count: {fontSize:'14px', color:'#1a1a1a', fontWeight:'600'},
  countInac: {fontSize:'13px', color:'#888'},
  btnAdd: {background:'#CC0000', color:'#fff', border:'none', borderRadius:'8px', padding:'10px 18px', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
  error: {background:'#fff0f0', border:'1px solid #ffcccc', color:'#CC0000', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px'},
  formCard: {background:'#fff', borderRadius:'12px', padding:'24px', marginBottom:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', border:'1px solid #e0e0e0'},
  formTitle: {fontSize:'15px', fontWeight:'700', color:'#1a1a1a', margin:'0 0 16px'},
  form: {display:'flex', flexDirection:'column', gap:'16px'},
  formGrid: {display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px'},
  group: {display:'flex', flexDirection:'column', gap:'6px'},
  label: {fontSize:'12px', fontWeight:'600', color:'#555'},
  input: {padding:'10px 12px', borderRadius:'7px', border:'1.5px solid #e0e0e0', fontSize:'14px', outline:'none', color:'#1a1a1a'},
  formBtns: {display:'flex', gap:'10px', justifyContent:'flex-end'},
  btnCancel: {padding:'10px 20px', borderRadius:'7px', border:'1.5px solid #e0e0e0', background:'#fff', color:'#666', fontSize:'13px', fontWeight:'600', cursor:'pointer'},
  btnSave: {padding:'10px 20px', borderRadius:'7px', border:'none', background:'#CC0000', color:'#fff', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
  empty: {textAlign:'center', padding:'60px', color:'#999', background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0'},
  list: {display:'flex', flexDirection:'column', gap:'8px'},
  card: {background:'#fff', borderRadius:'12px', border:'1px solid #e0e0e0', padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', boxShadow:'0 2px 6px rgba(0,0,0,0.05)'},
  cardInactivo: {opacity:0.5, borderStyle:'dashed'},
  cardLeft: {display:'flex', alignItems:'center', gap:'12px', flex:1},
  avatar: {width:'44px', height:'44px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'18px', fontWeight:'800', flexShrink:0},
  cardInfo: {flex:1},
  cardNombre: {fontSize:'14px', fontWeight:'700', color:'#1a1a1a'},
  cardEmail: {fontSize:'12px', color:'#888', margin:'2px 0'},
  rolBadge: {display:'inline-block', fontSize:'11px', fontWeight:'700', padding:'2px 8px', borderRadius:'10px'},
  cardBtns: {display:'flex', gap:'6px'},
  btnEdit: {padding:'7px 10px', borderRadius:'6px', border:'1.5px solid #e0e0e0', background:'#fff', cursor:'pointer', fontSize:'14px'},
  btnDel: {padding:'7px 10px', borderRadius:'6px', border:'none', background:'#fff0f0', cursor:'pointer', fontSize:'14px'},
  youBadge: {background:'#e8f4fd', color:'#1e40af', fontSize:'11px', fontWeight:'700', padding:'3px 10px', borderRadius:'10px'},
  inactivoBadge: {background:'#f0f0f0', color:'#888', fontSize:'11px', fontWeight:'700', padding:'3px 10px', borderRadius:'10px'},
  // ── Panel de permisos ──
  dosPaneles: {display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px, 1fr))', gap:'16px', marginBottom:'20px'},
  permSub: {fontSize:'13px', color:'#555', fontWeight:'600', borderBottom:'1px solid #eee', paddingBottom:'8px', marginBottom:'12px'},
  permTodos: {display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', fontWeight:'600', color:'#555', marginBottom:'12px', cursor:'pointer'},
  permLista: {display:'flex', flexDirection:'column', gap:'2px', maxHeight:'420px', overflowY:'auto'},
  permFila: {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 4px', borderBottom:'1px solid #f3f3f3', gap:'8px', flexWrap:'wrap'},
  permModulo: {fontSize:'13px', fontWeight:'700', color:'#1a1a1a', minWidth:'150px'},
  permAcciones: {display:'flex', gap:'14px', flexWrap:'wrap'},
  permAccion: {display:'flex', alignItems:'center', gap:'5px', fontSize:'12px', cursor:'pointer'},
  permCheck: {width:'15px', height:'15px', cursor:'pointer'},
  permLeyenda: {fontSize:'11px', marginTop:'10px', color:'#888'},
  permMsg: {fontSize:'13px', marginTop:'10px', fontWeight:'600'},
  btnOtorgar: {padding:'10px 22px', borderRadius:'7px', border:'none', background:'#e0a800', color:'#fff', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
};

export default Usuarios;
